import { useMemo } from 'react'
import { collection, query, where } from 'firebase/firestore'
import type { Query } from 'firebase/firestore'
import { discoveryRegionIds, EVERYWHERE_REGION_ID } from '@shared/regions'
import { db } from '../lib/firebase'
import { useFirestoreCollection } from './useFirestore'
import { todayLocalISO, yesterdayLocalISO } from '../lib/formatDate'
import type { Event } from '../types'

export type RegionEventsWindow = 'upcoming' | 'past'

/**
 * Discovery events for a region, filtered by Firestore on the stamped
 * `regionId`: parties from nearby regions, festivals from drivable ones (the
 * `isNearby` rule, so the client-side scope filter agrees with what's fetched).
 * Everywhere falls back to the unscoped query. `type` skips the other type's
 * listener (a region query only; Everywhere returns both). Pass
 * `enabled: false` to hold the listeners off.
 *
 * Unstamped events — drafts, and places no region maps — are never fetched
 * here; they only showed under Everywhere before, and still do.
 */
export function useRegionEvents(
  regionId: string,
  {
    window = 'upcoming',
    type,
    enabled = true,
  }: { window?: RegionEventsWindow; type?: Event['type']; enabled?: boolean } = {},
) {
  const boundary = window === 'upcoming' ? yesterdayLocalISO() : todayLocalISO()
  const [partyQuery, festivalQuery] = useMemo(() => {
    if (!enabled) return [null, null]
    const col = collection(db, 'events')
    const dateFilter = window === 'upcoming'
      ? where('startDate', '>=', boundary)
      : where('startDate', '<', boundary)
    if (regionId === EVERYWHERE_REGION_ID) return [query(col, dateFilter) as Query<Event>, null]
    const ids = discoveryRegionIds(regionId)
    const byType = (t: Event['type'], regionIds: string[]) =>
      regionIds.length === 0 || (type && type !== t)
        ? null
        : (query(col, where('type', '==', t), where('regionId', 'in', regionIds), dateFilter) as Query<Event>)
    return [byType('party', ids.party), byType('festival', ids.festival)]
  }, [regionId, window, boundary, type, enabled])

  const parties = useFirestoreCollection<Event>(partyQuery)
  const festivals = useFirestoreCollection<Event>(festivalQuery)

  const events = useMemo(
    () => [...parties.data, ...festivals.data].filter(e => e.published !== false),
    [parties.data, festivals.data],
  )
  // Two listeners, so `loading` (nothing to render) and `pending` (one still
  // owes its server snapshot — don't freeze yet) can differ. A first visit to
  // a region answers from the local cache with only the events some other
  // query already pulled in (friends' plans), so a cached result isn't enough.
  const pending = parties.loading || festivals.loading || parties.fromCache || festivals.fromCache
  return {
    events,
    loading: (parties.loading || festivals.loading) && events.length === 0,
    pending,
    error: parties.error ?? festivals.error,
  }
}
